import { ValuesCache } from '@leverege/value-cache';
export default class Paginator {
  constructor(options) {
    this.data = {
      items: []
    };
    this.options = options;
    this.pageCache = new ValuesCache();
  }

  /**
   * Sets the data, an object containing { count, perPage, page, items }
   */
  setData(data) {
    this.data = data;
  }

  /**
   * The number of pages
   */
  get pages() {
    return this.data.perPage > 0 && this.data.count > 0 ? Math.ceil(this.data.count / this.data.perPage) : 0;
  }

  /**
   * The number of items per page
   */
  get perPage() {
    return this.data.perPage > 0 ? this.data.perPage : 0;
  }

  /**
   * The total number of items
   */
  get count() {
    return this.data.count > 0 ? this.data.count : 0;
  }

  /**
   * Returns the current page number. The first page is zero. Use
   * displayPage to get the human readable page number
   */
  get page() {
    const pages = this.pages;
    if (this.data.page > pages) {
      return Math.max(pages - 1, 0);
    }
    return this.data.page > 0 ? this.data.page : 0;
  }

  /**
   * Returns the index of the first item on the page. This
   * is zero based. On page one of a ten item page, this index
   * will be ten.
   */
  get startIndex() {
    const page = this.page;
    const perPage = this.perPage;
    return page * perPage;
  }

  /**
   * Returns the index of the last item on the page. This
   * is zero based. On page one of a ten item page, this index
   * will be nineteen.
   */
  get endIndex() {
    const page = this.page;
    const perPage = this.perPage;
    return Math.min((page + 1) * perPage - 1, this.count - 1);
  }

  /**
   * Returns the page meant for a human. In particular, pages
   * start at one.
   */
  get displayPage() {
    return this.page + 1;
  }

  /**
   * Returns the page's start index meant for a human. In particular, indices
   * start at one.
   */
  get displayStartIndex() {
    return this.startIndex + 1;
  }

  /**
   * Returns the page's end index meant for a human. In particular, indices
   * start at one.
   */
  get displayEndIndex() {
    return this.endIndex + 1;
  }
  get pageItems() {
    const {
      items,
      perPage,
      count
    } = this.data;
    const page = this.page;
    return this.pageCache.calc(page, items => {
      const start = perPage * page;
      return items.slice(start, start + perPage);
    }, items, perPage, count);
  }
  get hasPreviousPage() {
    return this.displayStartIndex !== 1;
  }
  get hasNextPage() {
    const {
      count
    } = this.data;
    return this.displayEndIndex < count;
  }
  setPerPage(perPage) {
    const {
      onPageChange
    } = this.options;
    if (typeof perPage === 'number' && perPage > 0 && onPageChange) {
      const count = this.count;
      let page = this.page;
      if (count === 0) {
        page = 0;
      } else {
        const pages = Math.ceil(count / perPage);
        if (page >= pages) {
          page = Math.max(0, pages - 1);
        }
      }
      onPageChange({
        perPage,
        page
      });
    }
  }
  setPage(page) {
    const {
      onPageChange
    } = this.options;
    if (typeof page === 'number' && page >= 0 && page < this.pages && onPageChange && page !== this.page) {
      onPageChange({
        perPage: this.perPage,
        page
      });
    }
  }
  setPreviousPage() {
    const page = this.page;
    if (this.hasPreviousPage) {
      this.setPage(page - 1);
    }
  }
  setNextPage() {
    const page = this.page;
    if (this.hasNextPage) {
      this.setPage(page + 1);
    }
  }
}